import { Subject, Observable, combineLatest, merge, of } from 'rxjs';
import { Record } from "@common/data-models/records/record";
import { ObjectTypes } from "@common/enums/object-types";
import { Layout } from '@common/data-models/service/layout'
import { exhaustMap, map, concatMap, mergeMap } from 'rxjs/operators';

export class CollectionRecord extends Record {


    protected _children: Record[] = [];
    get children(): Record[] { return this._children; }
    set children(value: Record[]) { this._children = value; }
    public layout: Layout = null;
    public guidsToBeRemoved: Array<string> = [];

    totalCostObserver: Observable<number> = this.totalCostSubject.asObservable().pipe(
        map((cost) => {
            return cost;
        }),
        mergeMap((cost) => {
            return combineLatest([of(cost), ...this.children.map(c => c.totalCostObserver)]);
        }),
        map((costs: Array<number>) => {
            return costs.reduce((a, b) => a + b, 0);
        }
        ));

    public set(field: string, value: any) {
        super.set(field, value);
        this.updateToChild(field, value);
    }

    public collectionChangeSubject: Subject<void> = new Subject<void>();
    public collectionChangeObserver: Observable<void> = this.collectionChangeSubject.asObservable();

    private _collectionChangesTimeout: any = null;
    public scheduleChangeNotifier() {
        if (this._collectionChangesTimeout) {
            Record.timeoutQueue.clearTimeout(this._collectionChangesTimeout);
        }

        this._collectionChangesTimeout = Record.timeoutQueue.setTimeout(() => {
            this.collectionChangeSubject.next();
        }, 50);
    }

    public get isCollectionDiff(): boolean { return this.getAs<boolean>("IsCollectionDiff"); }
    public set isCollectionDiff(value: boolean) { this.set("IsCollectionDiff", value); }

    public get sequence(): Array<string> { return this.getAs<Array<string>>("Sequence"); }
    public set sequence(value: Array<string>) { this.set("Sequence", value); }

    constructor() {
        super();
        this.changeSubject.subscribe(() => {
            this.scheduleChangeNotifier();
        });
    }

    public getChildren(referal?: string, DataStore?: any): Array<Record> {
        // Register referals
        if (referal) {
            for (let child of this.children) DataStore.reference(referal, child.guid);
        }
        return this.children;
    }

    public setChildren(value: Array<Record>) {
        this.children = value;
        let children = this.getChildren();
        if (children.length > 0) {
            for (let child of children) {
                this.registerChildHooks(child);
            }
        }
        this.scheduleChangeNotifier();
    }

    public removeChildByGuid(guid: string) {
        for (let i = 0; i < this.children.length; i++) {
            if (this.children[i].guid == guid) {
                this.children[i].totalCostSubject.complete();
                this.children.splice(i, 1);
                return;
            }
        }
        this.scheduleChangeNotifier();
    }

    public removeChildByRef(child: Record) {
        let index = this.getChildren().indexOf(child);

        if (index > -1) {
            this.getChildren().splice(index, 1);
        }
        this.scheduleChangeNotifier();
        child.totalCostSubject.complete();
    }

    public addChildren(child: Record) {
        this.registerChildHooks(child);
        this.getChildren().push(child);
        this.scheduleChangeNotifier();
    }

    public sortChildren(f: (a: Record, b: Record) => number) {
        this.children.sort(f);
        this.scheduleChangeNotifier();
    }

    public removeChildren() {
        this.children.forEach(child => child.totalCostSubject.complete());
        this.children.splice(0, this.children.length);
    }

    public unshiftChildren(rec: Record) {
        this.registerChildHooks(rec);
        this.children.unshift(rec);
        this.scheduleChangeNotifier();
    }

    public registerChildHooks(rec: Record) {
        rec.parentRecord = this;
        rec.parentChanged();
        if (!rec.parentChangedSub) {
            rec.parentChangedSub = this.changeObserver.subscribe(rec.parentChanged.bind(rec));
        }
    }

    public hasChildWithGuid(guid: string): boolean {
        for (let i = 0; i < this.children.length; i++) {
            if (this.children[i].guid == guid) {
                return true;
            }
        }
        return false;
    }

    public copyDataAndChildrenTo(target: CollectionRecord, preserveGuids?: boolean, isTemplate?: boolean, fieldsToSkip?: { [field: string]: boolean }) {
        throw "CollectionRecord.copyDataAndChildrenTo() not implemented";
    }

    public updateToChild(field: string, value: any) {
        if (!this.guid) {
            if (this.children && this.children.length > 0) {
                Record.fieldMappingService.updateToChild(this, field, value);
            }
        }
    }

}
